import React, { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Button } from "@mui/material";
import { apiVariable } from "../../../../api/apiVariable";
import useAuth from "../../../../hooks/useAuth";
import StatusSnackbar from "../../../General/StatusSnackbar/StatusSnackbar";
import GenericFormPopup from "../../GenericFormPopup/GenericFormPopup";
import {
  harshEventFieldConfig,
  harshEventInitialValues,
} from "../../../../formConfigs/Fleet/harshEventFieldConfig";
import {
  fetchAllDrivers,
  fetchAssets,
} from "../../IncidentPopup/IncidentPopup";
import { getId } from "../../../HR/UpdateEmployeePopup/UpdateEmployeePopup";
import dayjs from "dayjs";

interface UpdateHarshEventPopupProps {
  harshEvent: any;
}

const UpdateHarshEventPopup: React.FC<UpdateHarshEventPopupProps> = ({
  harshEvent,
}) => {
  const [open, setOpen] = useState(false);
  const [initialValues, setInitialValues] = useState({
    ...harshEventInitialValues,
    date: dayjs(harshEvent.date).format("YYYY-MM-DD") || "",
    status: harshEvent.status || "",
    addedBy: harshEvent.addedBy || "",
  });
  const [updateStatus, setUpdateStatus] = useState<{
    open: boolean;
    success: boolean;
    message: string;
  }>({
    open: false,
    success: false,
    message: "",
  });

  const { token } = useAuth();
  const queryClient = useQueryClient();

  const { data: drivers } = useQuery({
    queryKey: ["drivers"],
    queryFn: () => fetchAllDrivers(token),
    enabled: open && !!token,
  });

  const { data: trucks } = useQuery({
    queryKey: ["trucks"],
    queryFn: () => fetchAssets("Truck", token),
    enabled: open && !!token,
  });

  useEffect(() => {
    if (harshEvent) {
      const formattedValues = {
        ...harshEvent,
        date: dayjs(harshEvent.date).format("YYYY-MM-DD"),
        driverId: harshEvent.driverId
          ? {
              id: harshEvent.driverId.toString(),
              label: harshEvent.driverName || "Unknown Driver",
            }
          : null,
        truckId: harshEvent.truckId
          ? {
              id: harshEvent.truckId.toString(),
              label: harshEvent.truckUnitNumber || "Unknown Truck",
            }
          : null,
      };
      setInitialValues(formattedValues);
    }
  }, [harshEvent]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
  };
  const handleSnackbarClose = () =>
    setUpdateStatus((prev) => ({ ...prev, open: false }));

  const mutation = useMutation({
    mutationFn: (data: any) =>
      axios.put(`${apiVariable}/harshEvent/${harshEvent.id}`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    onSuccess: () => {
      setUpdateStatus({
        open: true,
        success: true,
        message: "Harsh event updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["harshEvents"] });
      handleClose();
    },
    onError: (error: any) => {
      setUpdateStatus({
        open: true,
        success: false,
        message: error.response?.data || "Failed to update harsh event",
      });
    },
  });

  const submitHandler = (data: any) => {
    const finalData = {
      ...data,
      driverId: getId(data.driverId),
      truckId: getId(data.truckId),
    };
    mutation.mutate(finalData);
  };

  const dynamicFieldConfig = React.useMemo(() => {
    return harshEventFieldConfig.map((field) => {
      if (field.name === "driverId") {
        return { ...field, options: drivers || [] };
      }
      if (field.name === "truckId") {
        return { ...field, options: trucks || [] };
      }
      return field;
    });
  }, [drivers, trucks]);

  return (
    <>
      <Button variant="contained" onClick={handleOpen}>
        Update
      </Button>

      <GenericFormPopup
        open={open}
        onClose={handleClose}
        title="Update Harsh Event"
        initialValues={initialValues}
        autocompleteData={{ drivers: drivers || [], trucks: trucks || [] }}
        formFields={dynamicFieldConfig}
        onSubmit={submitHandler}
        loading={mutation.isPending}
      />

      <StatusSnackbar
        status={
          updateStatus.open
            ? updateStatus.success
              ? "success"
              : "error"
            : "idle"
        }
        successMessage={updateStatus.message}
        errorMessage={updateStatus.message}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default UpdateHarshEventPopup;
